import { addEvent } from '../utils/events';
import { SUPPORTS_HLS, loadScript } from '../utils/general';
import { isHlsSource } from '../utils/media';
import Native from './native';

//const Hls;

class HlsMedia extends Native {
    player = null;

    events = null;

    recoverDecodingErrorDate = null;

    recoverSwapAudioCodecDate = null;

    options = null;

    autoplay = false;
    startTime = 0;
    lastNudgePos: number = 0;
    nudgeCount: number = 0;
    constructor(element, mediaSource, autoplay, options, startTime) {
        super(element, mediaSource);
        this.options = options;
        this.videoEl = element;
        this.media = mediaSource;
        this.autoplay = autoplay;
        this.startTime = startTime;
        this.promise = (typeof Hls === 'undefined') ?
            // Ever-green script
            loadScript('https://cdn.jsdelivr.net/npm/hls.js@latest/dist/hls.min.js') :
            new Promise(resolve => resolve());

        this.promise.then(this._create.bind(this));
        return this;
    }

    canPlayType(mimeType) {
        return SUPPORTS_HLS() && mimeType === 'application/x-mpegURL';
    }

    load() {
        this.player.detachMedia();
        this.player.loadSource(this.media.src);
        this.player.attachMedia(this.videoEl);

        const e = addEvent('loadedmetadata');
        this.videoEl.dispatchEvent(e);

        if (!this.events) {
            this.events = Hls.Events;
            Object.keys(this.events).forEach(event => {
                this.player.on(this.events[event], (...args) => this._assign(this.events[event], args));
            });
        }
    }


    destroy() {
        this._revoke();
    }

    set src(media) {
        if (isHlsSource(media)) {
            this._revoke();
            this.player = new Hls();
            this.player.loadSource(media.src);
            this.player.attachMedia(this.videoEl);

            this.events = Hls.Events;
            Object.keys(this.events).forEach(event => {
                this.player.on(this.events[event], ...args => this._assign(this.events[event], args));
            });
        }
    }

    get levels() {
        const levels = [];
        if (this.player && this.player.levels && this.player.levels.length) {
            Object.keys(this.player.levels).forEach(item => {
                const { height, name } = this.player.levels[item];
                const level = {
                    height,
                    id: item,
                    label: name || null,
                };
                levels.push(level);
            });
        }
        return levels;
    }

    set level(level) {
        this.player.currentLevel = level;
    }

    get level() {
        return this.player ? this.player.currentLevel : -1;
    }

    _create() {
        let { options } = this;
        if (!options) {
            options = {};
        }
        if (this.startTime > 0) {
            options.startPosition = this.startTime;
        } else {
            options.startPosition = 1;
        }

        const autoplay = this.autoplay;

        this.player = new Hls(this.options);
        this.events = Hls.Events;
        Object.keys(this.events).forEach(event => {
            this.player.on(this.events[event], (...args) => this._assign(this.events[event], args));
        });

        if (!autoplay) {
            this.videoEl.addEventListener('play', () => {
                if (this.player) {
                    this.player.startLoad();
                }
            });

            this.videoEl.addEventListener('pause', () => {
                if (this.player) {
                    this.player.stopLoad();
                }
            });
        }
    }

    _assign(event, data) {
        if (event === 'hlsError') {
            const responseUrl = ',url-' + encodeURIComponent((data[1] && data[1].response && data[1].response.url) ? data[1].response.url : '')
            const errorDetails = {
                detail: {
                    type: 'HLS',
                    message: data[1].details,
                    data: data[1].error.message+responseUrl,
                    fatal: data[1].fatal
                },
            };
            const errorEvent = addEvent('playererror', errorDetails);
            //this.videoEl.dispatchEvent(errorEvent);
            data = data[1];

            // borrowed from https://video-dev.github.io/hls.js/demo
            const { type, fatal, ...details } = data;
            if (fatal) {
                switch (type) {
                    case 'mediaError':
                        const now = new Date().getTime();
                        if (!this.recoverDecodingErrorDate || (now - this.recoverDecodingErrorDate) > 3000) {
                            this.recoverDecodingErrorDate = new Date().getTime();
                            this.player.recoverMediaError();
                        } else if (!this.recoverSwapAudioCodecDate || (now - this.recoverSwapAudioCodecDate) > 3000) {
                            this.recoverSwapAudioCodecDate = new Date().getTime();
                            //console.warn('Attempting to swap Audio Codec and recover from media error');
                            this.player.swapAudioCodec();
                            this.player.recoverMediaError();
                        } else {
                            const msg = 'Cannot recover, last media error recovery failed';
                            //console.error(msg);
                            //const mediaEvent = addEvent(type, details);
                            this.videoEl.dispatchEvent(errorEvent);
                        }
                        break;
                    case 'networkError':
                        const message = 'Network error';
                        //console.error(message);
                        //const networkEvent = addEvent(type, details);
                        this.videoEl.dispatchEvent(errorEvent);
                        break;
                    default:
                        this.player.destroy();
                        //const fatalEvent = addEvent(type, details);
                        //this.videoEl.dispatchEvent(fatalEvent);
                        this.videoEl.dispatchEvent(errorEvent);
                        break;
                }
            } else {
                    if (data.details === Hls.ErrorDetails.BUFFER_NUDGE_ON_STALL) {
                        const currentPos = this.videoEl.currentTime;

                        // Check if we are stuck nudging at the same spot (within 100ms)
                        if (Math.abs(currentPos - this.lastNudgePos) < 0.1) {
                            this.nudgeCount++;
                        } else {
                            this.nudgeCount = 1;
                            this.lastNudgePos = currentPos;
                        }

                        // If we've nudged 3 times at the same spot, force a manual jump
                        if (this.nudgeCount >= 3) {
                            //console.warn("Tizen stuck loop detected. Forcing manual seek.");
                            this.videoEl.currentTime += 0.5; // Jump past the problematic frames
                            this.nudgeCount = 0;
                        }
                    }
                //const errorEvent = addEvent(type, details);
                this.videoEl.dispatchEvent(errorEvent);
            }
        } else {
            if(event == Hls.Events.FRAG_CHANGED) {
                this.nudgeCount = 0;
            }
            if (event === 'hlsLevelLoaded' && data[1].details.live === true) {
                //this.videoEl.setAttribute('op-live', 'true');
                const timeEvent = addEvent('timeupdate');
                this.videoEl.dispatchEvent(timeEvent);
            }
            const e = addEvent(event, data[1]);
            this.videoEl.dispatchEvent(e);
        }
    }

    _revoke() {
        this.videoEl.pause();
        this.player.stopLoad();
        this.player.detachMedia();
        this.player.removeAllListeners();
        this.player.destroy();
        this.player = null
        this.videoEl = null
    }
}

export default HlsMedia;
